import { createFileRoute } from "@tanstack/react-router";
import { EsRevisionSpokePage } from "@/components/site/EsRevisionSpokePage";
import { ES_REVISION_HOW_PERFORMED, ES_REVISION_SPOKE_BY_PAGE } from "@/lib/es-revision-spokes";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";
import { PHYSICIAN_REF } from "@/lib/medical-schema";
import { PROCEDURE_MEDIA, procedureImageObjects } from "@/lib/procedure-media";

// The page key and path are inlined so the full Spanish revision content
// model is referenced only from the component and the server-only schema
// block below, keeping it out of the shared client bundle.
const PAGE = "revisionsBand" as const;
const url = `${SITE}/es/cirugia-de-revision/banda-gastrica`;

/** Clinical images this page renders, in DOM order. */
const PAGE_IMAGE_KEYS = ["bandTubing", "bandDilatedEsophagus", "bandRemoved"] as const;
const PAGE_IMAGES = procedureImageObjects(PAGE_IMAGE_KEYS, "es", url);
const SCAN = PROCEDURE_MEDIA.bandDilatedEsophagus;
const SCAN_URL = `${SITE}${SCAN.src}`;

export const Route = createFileRoute("/es/cirugia-de-revision_/banda-gastrica")({
  head: () => ({
    ...esHead(PAGE),
    meta: esHead(PAGE).meta.map((entry) => {
      if (entry.property === "og:image" || entry.name === "twitter:image") {
        return { ...entry, content: SCAN_URL };
      }
      if (entry.property === "og:image:alt" || entry.name === "twitter:image:alt") {
        return { ...entry, content: SCAN.alt.es };
      }
      if (entry.property === "og:image:type") return { ...entry, content: "image/jpeg" };
      if (entry.property === "og:image:width") return { ...entry, content: String(SCAN.width) };
      if (entry.property === "og:image:height") return { ...entry, content: String(SCAN.height) };
      return entry;
    }),
    links: [
      ...esHead(PAGE).links,
      // Preload the scan's WebP the <picture> element actually uses.
      // imagesrcset/imagesizes must mirror the figure's <source> so the
      // browser preloads the same candidate it will select (800w on most
      // screens) instead of force-fetching the 1268w file a second time.
      {
        rel: "preload",
        as: "image",
        href: SCAN.webp,
        imagesrcset: SCAN.webpSrcSet,
        imagesizes: "(min-width: 768px) 300px, calc(100vw - 3rem)",
        type: "image/webp",
      },
    ],
    scripts: import.meta.env.SSR
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  ...esWebPageSchema(PAGE),
                  "@type": "MedicalWebPage",
                  lastReviewed: ES_REVISION_SPOKE_BY_PAGE.revisionsBand.lastReviewed,
                  reviewedBy: PHYSICIAN_REF,
                  mainEntity: { "@id": `${url}#procedure` },
                   primaryImageOfPage: PAGE_IMAGES[1],
                  image: PAGE_IMAGES,
                },
                {
                  "@type": "MedicalProcedure",
                  "@id": `${url}#procedure`,
                  mainEntityOfPage: { "@id": `${url}#webpage` },
                  procedureType: "https://schema.org/SurgicalProcedure",
                  name: ES_REVISION_SPOKE_BY_PAGE.revisionsBand.procedureName,
                  alternateName: ES_REVISION_SPOKE_BY_PAGE.revisionsBand.procedureAlternateNames,
                  description: ES_REVISION_SPOKE_BY_PAGE.revisionsBand.procedureDescription,
                  url,
                  howPerformed: ES_REVISION_HOW_PERFORMED[PAGE],
                  image: PAGE_IMAGES,
                },
                {
                  "@type": "FAQPage",
                  "@id": `${url}#faq`,
                  inLanguage: "es-US",
                  mainEntity: ES_REVISION_SPOKE_BY_PAGE.revisionsBand.faqs.map((faq, i) => ({
                    "@type": "Question",
                    "@id": `${url}#faq-${i + 1}`,
                    name: faq.q,
                    acceptedAnswer: { "@type": "Answer", text: faq.a },
                  })),
                },
              ],
            }),
          },
        ]
      : [],
  }),
  component: EsBandRevision,
});

function EsBandRevision() {
  return <EsRevisionSpokePage spoke={ES_REVISION_SPOKE_BY_PAGE.revisionsBand} />;
}
